import { useLocation, useNavigate } from 'react-router-dom'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'

const TIER_CONFIG = {
    low: { color: 'text-green-400', border: 'border-green-500/30', bg: 'bg-green-500/5', bar: 'bg-green-400', label: 'Low Risk' },
    moderate: { color: 'text-yellow-400', border: 'border-yellow-500/30', bg: 'bg-yellow-500/5', bar: 'bg-yellow-400', label: 'Moderate Risk' },
    elevated: { color: 'text-orange-400', border: 'border-orange-500/30', bg: 'bg-orange-500/5', bar: 'bg-orange-400', label: 'Elevated Risk' },
    critical: { color: 'text-red-400', border: 'border-red-500/30', bg: 'bg-red-500/5', bar: 'bg-red-400', label: 'Critical Risk' },
}

export default function Results() {
    const { state } = useLocation()
    const navigate = useNavigate()
    const result = state?.result

    if (!result) {
        return (
            <div className="min-h-screen bg-brand-navy flex flex-col">
                <Navbar />
                <div className="flex-1 flex items-center justify-center">
                    <div className="text-center">
                        <p className="text-brand-muted font-body text-sm mb-4">
                            No results found. Please run an analysis first.
                        </p>
                        <button onClick={() => navigate('/analyze')} className="btn-primary">
                            Analyse a message
                        </button>
                    </div>
                </div>
                <Footer />
            </div>
        )
    }

    const pass1 = result.pass1
    const pass2 = result.pass2
    const tier = pass1?.tier?.toLowerCase() || 'low'
    const config = TIER_CONFIG[tier] || TIER_CONFIG.low
    const score = pass1?.score ?? 0

    return (
        <div className="min-h-screen bg-brand-navy flex flex-col">
            <Navbar />
            <div className="flex-1 max-w-4xl mx-auto px-4 sm:px-6 py-12 sm:py-16 w-full">

                <div className="mb-10">
                    <h1 className="font-display text-3xl sm:text-4xl font-bold text-brand-text mb-3">
                        Analysis complete
                    </h1>
                    <p className="text-brand-muted font-body text-sm">
                        Your message has been professionally analysed in depth.
                        Scroll down for the full report.
                    </p>
                </div>

                {/* Risk score banner */}
                <div className={`border ${config.border} ${config.bg} rounded-lg p-6 mb-6`}>
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <p className="text-brand-muted text-xs font-body uppercase tracking-widest mb-1">
                                Risk assessment
                            </p>
                            <p className={`font-display text-3xl font-bold ${config.color}`}>
                                {config.label}
                            </p>
                        </div>
                        <div className="text-right">
                            <p className="text-brand-muted text-xs font-body uppercase tracking-widest mb-1">
                                Score
                            </p>
                            <p className={`font-display text-5xl font-bold ${config.color}`}>
                                {score}
                            </p>
                        </div>
                    </div>
                    <div className="h-1.5 bg-brand-border rounded-full overflow-hidden">
                        <div
                            className={`h-full rounded-full transition-all duration-700 ${config.bar}`}
                            style={{ width: `${score}%` }}
                        />
                    </div>
                </div>

                {/* Active flags */}
                {pass1?.flags && (
                    <div className="flex flex-wrap gap-2 mb-6">
                        {Object.entries(pass1.flags).map(([flag, active]) =>
                            active ? (
                                <span key={flag}
                                    className="border border-brand-border bg-brand-surface text-brand-muted
                             text-xs font-body px-3 py-1 rounded-full">
                                    {flag.replace(/_/g, ' ')}
                                </span>
                            ) : null
                        )}
                    </div>
                )}

                {/* Triggered phrases */}
                {pass1?.triggered_phrases?.length > 0 && (
                    <div className="card mb-6">
                        <h2 className="font-display text-lg font-semibold text-brand-text mb-4">
                            Flagged phrases
                        </h2>
                        <div className="space-y-4">
                            {pass1.triggered_phrases.map((phrase, i) => (
                                <div key={i}
                                    className="border-b border-brand-border pb-4 last:border-0 last:pb-0">
                                    <p className="text-brand-text text-sm font-mono font-medium mb-1">
                                        "{phrase.phrase}"
                                    </p>
                                    <div className="flex items-center gap-3 flex-wrap">
                                        <span className="text-brand-muted text-xs font-body">
                                            {phrase.mechanism?.replace(/_/g, ' ')}
                                        </span>
                                        <span className={`text-xs font-body font-medium ${phrase.severity === 'critical' ? 'text-red-400' :
                                                phrase.severity === 'elevated' ? 'text-orange-400' :
                                                    'text-yellow-400'
                                            }`}>
                                            {phrase.severity}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Pass 2 mechanism narratives */}
                {pass2?.mechanism_narratives?.length > 0 && (
                    <div className="card mb-6">
                        <h2 className="font-display text-lg font-semibold text-brand-text mb-4">
                            Expert assessment
                        </h2>
                        <div className="space-y-6">
                            {pass2.mechanism_narratives.map((item, i) => (
                                <div key={i}
                                    className="border-b border-brand-border pb-6 last:border-0 last:pb-0">
                                    <p className="text-brand-amber text-xs font-body uppercase tracking-widest mb-2">
                                        {item.mechanism?.replace(/_/g, ' ')}
                                    </p>
                                    <p className="text-brand-text font-body text-sm leading-relaxed mb-3">
                                        {item.narrative}
                                    </p>
                                    {item.consequence && (
                                        <div className={`border-l-2 ${config.border} pl-3`}>
                                            <p className="text-brand-muted text-xs font-body uppercase tracking-widest mb-1">
                                                Likely consequence
                                            </p>
                                            <p className="text-brand-muted font-body text-sm leading-relaxed">
                                                {item.consequence}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Inference chain */}
                {pass2?.inference_chain?.steps?.length > 0 && (
                    <div className="card mb-6">
                        <h2 className="font-display text-lg font-semibold text-brand-text mb-4">
                            Risk pathway
                        </h2>
                        <div className="space-y-3 mb-4">
                            {pass2.inference_chain.steps.map((step, i) => (
                                <div key={i} className="flex items-start gap-3">
                                    <div className={`w-5 h-5 rounded-full border ${config.border} flex items-center
                                  justify-center shrink-0 mt-0.5`}>
                                        <span className={`text-xs font-mono font-bold ${config.color}`}>{i + 1}</span>
                                    </div>
                                    <p className="text-brand-muted font-body text-sm leading-relaxed">{step}</p>
                                </div>
                            ))}
                        </div>
                        {pass2.inference_chain.summary && (
                            <p className={`font-body text-sm font-medium ${config.color} border-t border-brand-border pt-4`}>
                                {pass2.inference_chain.summary}
                            </p>
                        )}
                    </div>
                )}

                {/* Rewrite */}
                {pass2?.rewrite?.rewritten_message && (
                    <div className="card mb-8 border-brand-amber/40">
                        <h2 className="font-display text-lg font-semibold text-brand-amber mb-4">
                            Suggested rewrite
                        </h2>
                        <p className="text-brand-text font-body text-sm leading-relaxed whitespace-pre-line mb-4">
                            {pass2.rewrite.rewritten_message}
                        </p>
                        {pass2.rewrite.removed_element && (
                            <div className="border-t border-brand-border pt-4">
                                <p className="text-brand-muted text-xs font-body uppercase tracking-widest mb-1">
                                    What was changed
                                </p>
                                <p className="text-brand-muted font-body text-sm leading-relaxed">
                                    {pass2.rewrite.removed_element}
                                </p>
                            </div>
                        )}
                        {pass2.rewrite.intent_preserved && (
                            <div className="border-t border-brand-border pt-4 mt-4">
                                <p className="text-brand-muted text-xs font-body uppercase tracking-widest mb-1">
                                    Intent preserved
                                </p>
                                <p className="text-brand-muted font-body text-sm leading-relaxed">
                                    {pass2.rewrite.intent_preserved}
                                </p>
                            </div>
                        )}
                    </div>
                )}

                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                    <button onClick={() => navigate('/analyze')} className="btn-primary w-full sm:w-auto text-center">
                        Analyse another message
                    </button>
                    <button onClick={() => navigate('/plans')} className="btn-ghost w-full sm:w-auto text-center">
                        View plans
                    </button>
                </div>
            </div>
            <Footer />
        </div>
    )
}